import { useMemo, useState, type FC } from "react";
import ChartSwitcher from "@/components/ChartSwitcher";
import useDarkMode from "@/hooks/useDarkMode";
import {
  buildVideoBarOption,
  buildVideoCurveOption,
} from "@/pages/CampaignOverviewPage/charts/videoFunnelChartOptions";
import VideoFunnel from "@/pages/CampaignOverviewPage/components/VideoFunnel";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import {
  buildFunnelShapes,
  extractVideoStages,
  hasVideoData,
} from "@/pages/CampaignOverviewPage/utils/videoFunnelStages";
import { pillBarChartHeightRem } from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import { fmtPct } from "@/utils/tableFormatters";
import styles from "@/pages/CampaignOverviewPage/widgets/VideoFunnelWidget.module.css";

type VideoChartType = "funnel" | "horizontalBar" | "curve";

const CHART_OPTIONS: { key: VideoChartType; label: string }[] = [
  { key: "funnel", label: TEXT.campaignOverview.videoQuartiles.typeFunnel },
  {
    key: "horizontalBar",
    label: TEXT.campaignOverview.videoQuartiles.typeHorizontalBar,
  },
  { key: "curve", label: TEXT.campaignOverview.videoQuartiles.typeCurve },
];

interface VideoFunnelWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

const VideoFunnelWidget: FC<VideoFunnelWidgetProps> = ({
  error,
  isLoading,
  rows,
}) => {
  const isDark = useDarkMode();
  const [chartType, setChartType] = useState<VideoChartType>("funnel");

  // video_start === 0 / null means non-video campaign — expected empty state,
  // not an error. Treat no rows the same way.
  const isEmpty = !hasVideoData(rows);

  const vcr =
    isEmpty || isLoading || error
      ? null
      : (rows[0]?.video_complete_rate ?? null);

  const option = useMemo(() => {
    if (chartType === "horizontalBar") return buildVideoBarOption(rows, isDark);
    if (chartType === "curve") return buildVideoCurveOption(rows, isDark);
    return undefined;
  }, [rows, chartType, isDark]);
  const funnelShapes = buildFunnelShapes(extractVideoStages(rows));

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <span className={styles.title}>
            {TEXT.campaignOverview.videoQuartiles.title}
          </span>
          {vcr !== null && (
            <span className={styles.vcrStat}>
              {TEXT.campaignOverview.videoQuartiles.vcr} {fmtPct(vcr)}
            </span>
          )}
        </div>
        <ChartSwitcher
          label={TEXT.campaignOverview.switcher.chartType}
          options={CHART_OPTIONS}
          value={chartType}
          onChange={setChartType}
        />
      </div>
      <ChartStateBody
        error={error}
        errorMessage={TEXT.campaignOverview.videoQuartiles.error}
        isEmpty={isEmpty}
        isLoading={isLoading}
        loadingClassName={styles.loading}
        messageClassName={styles.message}
        noDataMessage={TEXT.campaignOverview.videoQuartiles.noData}
        option={option}
        style={
          chartType === "horizontalBar"
            ? {
                height: pillBarChartHeightRem(funnelShapes.length),
                width: "100%",
              }
            : undefined
        }
      >
        {chartType === "funnel" && <VideoFunnel shapes={funnelShapes} />}
      </ChartStateBody>
    </div>
  );
};

export default VideoFunnelWidget;
